import React, { useCallback, useEffect, useState } from "react";
import { Plus, Loader2, Briefcase, Trash2, Edit2, MapPin } from "lucide-react";
import {
  getJobsAdminAPI,
  updateJobAPI,
  deleteJobAPI,
} from "../services/apicollection";
import JobForm from "../Components/JobForm";

const JobManagementPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const controller = new AbortController();

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getJobsAdminAPI({ signal: controller.signal });
      if (res?.status && Array.isArray(res.data?.jobs)) {
        setJobs(res.data.jobs);
      } else {
        setJobs([]);
        setError(res?.message || "Failed to load jobs");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load jobs");
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    return () => controller.abort();
  }, [fetchJobs]);

  const handleSave = () => {
    setShowForm(false);
    setEditingJob(null);
    fetchJobs();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingJob(null);
  };

  const handleTogglePublish = async (job) => {
    setTogglingId(job._id);
    try {
      const res = await updateJobAPI(job._id, { isPublished: !job.isPublished });
      if (res?.status) {
        fetchJobs();
      } else {
        alert(res?.message || "Failed to update");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    try {
      const res = await deleteJobAPI(id);
      if (res?.status) {
        fetchJobs();
      } else {
        alert(res?.message || "Failed to delete");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete");
    }
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <JobForm onSuccess={handleSave} onCancel={handleCancel} />
      </div>
    );
  }

  if (editingJob) {
    return (
      <div className="space-y-6">
        <JobForm initialJob={editingJob} onSuccess={handleSave} onCancel={handleCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">Jobs</h2>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          <Plus size={16} />
          Post job
        </button>
      </div>

      <p className="text-xs text-slate-500">
        Published jobs appear on the public site at <span className="font-mono">/jobs</span>.
      </p>

      {loading ? (
        <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12">
          <Loader2 size={28} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-4 text-center text-sm text-red-700">
          {error}
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-12 text-center">
          <Briefcase size={36} className="text-slate-300" />
          <p className="mt-2 text-sm text-slate-500">No jobs yet</p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="mt-2 text-sm font-medium text-cyan-700 underline hover:no-underline"
          >
            Post your first job
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {jobs.map((job) => (
            <article
              key={job._id}
              className={`flex flex-col overflow-hidden rounded-lg border shadow-sm transition-shadow hover:shadow ${
                job.isPublished
                  ? "border-[#1d3350] bg-[#0f1c2e]"
                  : "border-[#1d3350] bg-[#0d1b2e] opacity-90"
              }`}
            >
              <div className="flex gap-3 p-4">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-[#1d3350] bg-[#0f1c2e]">
                  {job.companyLogoUrl ? (
                    <img
                      src={job.companyLogoUrl}
                      alt=""
                      className="h-full w-full object-contain p-1"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[#13233a] text-xs font-semibold text-slate-500">
                      {job.companyName?.slice(0, 2)?.toUpperCase() || "—"}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-sm font-semibold text-slate-100">{job.positionName}</h4>
                  <p className="text-xs text-slate-400">{job.companyName}</p>
                  <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                    <MapPin size={12} />
                    {job.location}
                    <span className="text-slate-400">·</span>
                    <span className="capitalize">{job.locationType}</span>
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#17283f] px-3 py-2">
                <label className="flex cursor-pointer items-center gap-2 text-xs">
                  <span className="text-slate-400">Published:</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={job.isPublished}
                    onClick={() => handleTogglePublish(job)}
                    disabled={togglingId === job._id}
                    className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none ${
                      job.isPublished ? "bg-cyan-600" : "bg-slate-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-[#0f1c2e] transition-transform ${
                        job.isPublished ? "translate-x-4" : "translate-x-0.5"
                      }`}
                    />
                  </button>
                </label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditingJob(job)}
                    className="rounded border border-[#1d3350] px-2 py-1 text-xs text-slate-400 hover:bg-[#0d1b2e]"
                  >
                    <Edit2 size={12} className="inline" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(job._id)}
                    className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={12} className="inline" /> Delete
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default JobManagementPage;
